import React, { forwardRef, useState } from 'react';
import classNames from 'classnames';
import { Link, NavLink } from 'react-router-dom';
import { Disclosure, Transition } from '@headlessui/react';
import { getFeatureFlagValue } from 'src/utils/getFeatureFlagValue';
import { Icon } from 'src/components/Icon';
import { trpc } from 'src/lib/trpc';
import { useI18nContext } from 'src/i18n/i18n-react';
import Menu01 from 'src/assets/svgicons/duocolor/menu-01.svg';
import XClose from 'src/assets/svgicons/duocolor/x-close.svg';
import MagicBriefBolt from 'src/assets/svgicons/magicbrief/MagicBriefBolt.svg';
import MagicBriefLogoHalfColoured from 'src/assets/svgicons/magicbrief/MagicBriefLogoHalfColoured.svg';
import UserPlus01 from 'src/assets/svgicons/line/user-plus-01.svg';
import { useFirebaseContext } from 'src/pages/outlets/FirebaseOutlet/useFirebaseContext';
import NotificationsPopup from 'src/components/Notifications';
import { BaseButton, BaseButtonProps } from '../Button';
import { ButtonIcon } from '../Button/Button';
import UserMenuDropdown from '../UserMenuDropdown';
import InviteTeamModal from '../InviteTeamModal/InviteTeamModal';
import {
  NavigationContext,
  useDefaultNavigationLinks,
  useNavigationContext,
} from './hooks';

type NavigationComposite = {
  Section: React.FunctionComponent<
    React.PropsWithChildren<NavigationSectionProps>
  >;
  Button: React.FunctionComponent<BaseButtonProps & NavigationButtonProps>;
  Link: React.FunctionComponent<
    React.PropsWithChildren<NavigationNavLinkProps>
  >;
  DefaultLinks: React.FunctionComponent;
  SkeletonLinks: React.FunctionComponent;
  DefaultLead: React.FunctionComponent;
  Base: React.FunctionComponent<NavigationBaseProps>;
};

type NavigationBaseProps = React.PropsWithChildren<{
  mobileMenu: React.ReactNode | null;
  staticPosition?: boolean;
}>;

const NavigationBase: React.FunctionComponent<NavigationBaseProps> = ({
  mobileMenu,
  children,
  staticPosition,
}) => {
  return (
    <Disclosure
      as="nav"
      className={classNames(
        staticPosition ? 'static' : 'sticky top-0',
        'z-300 w-full bg-purple-900'
      )}
    >
      {({ open }) => (
        <NavigationContext.Provider value={open}>
          <div className="flex h-[72px] flex-row items-center justify-between px-5 py-2">
            {children}
          </div>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform -translate-y-4 opacity-0"
            enterTo="transform translate-y-0 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform tranlsate-y-0 opacity-100"
            leaveTo="transform -translate-y-4 opacity-0"
          >
            {mobileMenu && (
              <Disclosure.Panel className="mobile group px-6 pb-6 pt-3 transition-all lg:hidden">
                {mobileMenu}
              </Disclosure.Panel>
            )}
          </Transition>
        </NavigationContext.Provider>
      )}
    </Disclosure>
  );
};

type NavigationProps = React.PropsWithChildren<{
  customSections?: React.ReactNode | null;
  guest?: boolean;
}>;

const Navigation: React.FunctionComponent<NavigationProps> &
  NavigationComposite = ({ children, customSections, guest }) => {
  const { LL } = useI18nContext();
  const [showInviteModal, setShowInviteModal] = useState(false);
  return (
    <NavigationBase mobileMenu={<NavigationDefaultMobileLinks />}>
      {guest ? (
        <>
          <a href="/" className="hidden h-7 lg:block">
            <MagicBriefLogoHalfColoured className="hidden h-full max-w-[143px] lg:block" />
          </a>
          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-purple-500 lg:hidden">
            <div className="text-white">
              <Icon>
                <MagicBriefBolt />
              </Icon>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-row items-center">
          <NavigationDefaultLead />
          <div className="hidden flex-row items-center gap-3 px-6 lg:flex">
            <NavigationDefaultLinks />
          </div>
        </div>
      )}
      {customSections}
      <Navigation.Section>
        {guest && (
          <div className="flex items-center gap-4">
            <p className="hidden text-sm font-medium text-white lg:block">
              {LL.libraryTryEntice()}
            </p>
            <Link className="no-underline" to="/register">
              <Navigation.Button className="w-[137px]">
                {LL.try()}
              </Navigation.Button>
            </Link>
          </div>
        )}
        {children}
        {!guest && (
          <>
            <NavigationButton
              variant="custom"
              onClick={() => setShowInviteModal(true)}
              className="bg-purple-800 hidden lg:block"
            >
              <span className="flex flex-row items-center gap-2">
                <span className="hidden lg:block">{LL.invite()}</span>
                <Icon>
                  <UserPlus01 />
                </Icon>
              </span>
            </NavigationButton>
            <InviteTeamModal
              show={showInviteModal}
              onClose={() => setShowInviteModal(false)}
            />
          </>
        )}
        {!guest && getFeatureFlagValue('NOTIFICATIONS_IN_APP') && (
          <NotificationsPopup />
        )}
        {!guest && <UserMenuDropdown />}
      </Navigation.Section>
    </NavigationBase>
  );
};

type NavigationSectionProps = {
  className?: string;
};

const NavigationSection: React.FunctionComponent<
  React.PropsWithChildren<NavigationSectionProps>
> = ({ children, className }) => {
  return (
    <div
      className={classNames(className, 'flex flex-row items-center gap-2.5')}
    >
      {children}
    </div>
  );
};

const baseNavigationItemStyle =
  'text-white font-bold text-sm px-3 py-3 rounded-lg h-12 disabled:opacity-50 disabled:cursor-not-allowed';

type NavigationButtonProps = {
  variant?: 'normal' | 'custom';
};

const NavigationButton = forwardRef<
  HTMLButtonElement,
  BaseButtonProps & NavigationButtonProps
>(({ variant = 'normal', className, icon, children, ...props }, ref) => {
  return (
    <BaseButton
      {...props}
      ref={ref}
      icon={icon ? <ButtonIcon size="normal">{icon}</ButtonIcon> : null}
      className={classNames(
        baseNavigationItemStyle,
        variant === 'normal' ? 'bg-purple-800 hover:bg-purple-700' : '',
        icon && !children ? 'w-12' : '',
        className
      )}
    >
      {children}
    </BaseButton>
  );
});

NavigationButton.displayName = 'NavigationButton';

type NavigationNavLinkProps = {
  to: string;
};

const baseNavigationLinkStyle = `px-4 h-9 text-white font-semibold leading-7 flex rounded-full justify-center items-center group-[.mobile]:h-9 group-[.mobile]:justify-start`;

const NavigationSkeletonNavLink: React.FunctionComponent<
  React.PropsWithChildren
> = ({ children }) => {
  return <div className={baseNavigationLinkStyle}>{children}</div>;
};

const NavigationNavLink: React.FunctionComponent<
  React.PropsWithChildren<NavigationNavLinkProps>
> = ({ children, to }) => {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        classNames(
          baseNavigationLinkStyle,
          isActive ? 'bg-purple-800' : 'hover:bg-purple-800'
        )
      }
    >
      {children}
    </NavLink>
  );
};

const NavigationDefaultLinks: React.FunctionComponent = () => {
  const links = useDefaultNavigationLinks();
  return (
    <div className="flex flex-row gap-2">
      {links.map(({ to, label }) => (
        <NavigationNavLink key={to} to={to}>
          {label}
        </NavigationNavLink>
      ))}
    </div>
  );
};

const NavigationSkeletonDefaultLinks: React.FunctionComponent = () => {
  const links = useDefaultNavigationLinks();
  return (
    <div className="flex flex-row gap-2">
      {links.map(({ to, label }) => (
        <NavigationSkeletonNavLink key={to}>
          <span className="rounded-full bg-white/20 animate-pulse">
            <span aria-hidden className="invisible">
              {label}
            </span>
          </span>
        </NavigationSkeletonNavLink>
      ))}
    </div>
  );
};

const NavigationDefaultMobileLinks: React.FunctionComponent = () => {
  const links = useDefaultNavigationLinks();
  const { LL } = useI18nContext();
  const [showInviteModal, setShowInviteModal] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      {links.map(({ to, label }) => (
        <NavigationNavLink key={to} to={to}>
          {label}
        </NavigationNavLink>
      ))}
      <NavigationButton
        variant="custom"
        onClick={() => setShowInviteModal(true)}
        className="bg-purple-800"
      >
        <span className="flex flex-row items-center gap-2">
          <span className="hidden lg:block">{LL.invite()}</span>
          <Icon>
            <UserPlus01 />
          </Icon>
        </span>
      </NavigationButton>
      <InviteTeamModal
        show={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />
    </div>
  );
};

const NavigationDefaultLead: React.FunctionComponent = () => {
  const { isMobileMenuOpen } = useNavigationContext();
  const { LL } = useI18nContext();
  const user = useFirebaseContext();
  const org = trpc.organisation.getOrganisation.useQuery(undefined, {
    enabled: !!user?.emailVerified,
  });

  return (
    <div className="flex flex-row items-center gap-3">
      <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white lg:hidden">
        <span className="sr-only">{LL.navigation.openMenu()}</span>
        {isMobileMenuOpen ? (
          <Icon className="block h-6 w-6" aria-hidden="true">
            <XClose />
          </Icon>
        ) : (
          <Icon className="block h-6 w-6" aria-hidden="true">
            <Menu01 />
          </Icon>
        )}
      </Disclosure.Button>
      <div className="hidden h-12 w-12 items-center justify-center overflow-hidden rounded-lg bg-purple-500 lg:flex">
        {org.data?.profilePicURL ? (
          <img
            alt=""
            className="h-full object-cover"
            src={org.data.profilePicURL}
          />
        ) : (
          <div className="text-white">
            <Icon>
              <MagicBriefBolt />
            </Icon>
          </div>
        )}
      </div>
    </div>
  );
};

Navigation.Section = NavigationSection;
Navigation.Button = NavigationButton;
Navigation.Link = NavigationNavLink;
Navigation.SkeletonLinks = NavigationSkeletonDefaultLinks;
Navigation.DefaultLinks = NavigationDefaultLinks;
Navigation.DefaultLead = NavigationDefaultLead;
Navigation.Base = NavigationBase;

export default Navigation;
